import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { hiringApi } from "@/api/hiring";
import type { HireProposal, CreateHireProposalInput } from "@/api/hiring";
import { queryKeys } from "@/lib/query-keys";
import { UserPlus, Plus, Send, Rocket, ChevronDown, ChevronUp } from "lucide-react";

const statusVariant: Record<string, "success" | "warning" | "danger" | "default" | "secondary"> = {
  draft: "secondary",
  pending_approval: "warning",
  approved: "success",
  activated: "success",
  rejected: "danger",
};

const roles = ["ic", "manager", "general"] as const;
const targetTypes = ["existing_instance_subagent", "new_azure_instance"] as const;

export function Hiring() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const { data: proposals = [], isLoading } = useQuery({
    queryKey: queryKeys.hiring.byCompany(tenantId),
    queryFn: () => hiringApi.list(tenantId),
  });

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<Partial<CreateHireProposalInput>>({
    proposedName: "",
    proposedRole: "ic",
    proposedTitle: "",
    hireTargetType: "existing_instance_subagent",
    justification: "",
    proposedReportsTo: null,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.hiring.byCompany(tenantId) });

  const proposeMutation = useMutation({
    mutationFn: () =>
      hiringApi.propose({
        companyId: tenantId,
        proposedName: form.proposedName!,
        proposedRole: form.proposedRole,
        proposedTitle: form.proposedTitle || undefined,
        hireTargetType: form.hireTargetType as CreateHireProposalInput["hireTargetType"],
        justification: form.justification || undefined,
        proposedReportsTo: form.proposedReportsTo || undefined,
      }),
    onSuccess: () => {
      invalidate();
      setShowCreate(false);
      setForm({ proposedName: "", proposedRole: "ic", proposedTitle: "", hireTargetType: "existing_instance_subagent", justification: "", proposedReportsTo: null });
    },
  });

  const submitMutation = useMutation({
    mutationFn: (id: string) => hiringApi.submit(id),
    onSuccess: invalidate,
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => hiringApi.activate(id),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.byCompany(tenantId) });
    },
  });

  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <CenterPanel title="Hiring Proposals">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          {proposals.length} proposal{proposals.length !== 1 ? "s" : ""}
        </p>
        <Button size="sm" onClick={() => setShowCreate(!showCreate)}>
          <Plus size={16} className="mr-1.5" />
          New Proposal
        </Button>
      </div>

      {showCreate && (
        <Card className="mb-4">
          <CardContent className="space-y-3 pt-4">
            <div className="grid grid-cols-2 gap-3">
              <Input
                placeholder="Agent name"
                value={form.proposedName}
                onChange={(e) => setForm({ ...form, proposedName: e.target.value })}
              />
              <Input
                placeholder="Title (optional)"
                value={form.proposedTitle}
                onChange={(e) => setForm({ ...form, proposedTitle: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <select
                value={form.proposedRole}
                onChange={(e) => setForm({ ...form, proposedRole: e.target.value })}
                className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
              >
                {roles.map((r) => (
                  <option key={r} value={r}>{r.toUpperCase()}</option>
                ))}
              </select>
              <select
                value={form.hireTargetType}
                onChange={(e) => setForm({ ...form, hireTargetType: e.target.value as CreateHireProposalInput["hireTargetType"] })}
                className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
              >
                {targetTypes.map((t) => (
                  <option key={t} value={t}>
                    {t === "existing_instance_subagent" ? "Sub-agent" : "New Azure VM"}
                  </option>
                ))}
              </select>
              <select
                value={form.proposedReportsTo ?? ""}
                onChange={(e) => setForm({ ...form, proposedReportsTo: e.target.value || null })}
                className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
              >
                <option value="">Reports to... (optional)</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.name} ({a.role})</option>
                ))}
              </select>
            </div>
            <Input
              placeholder="Justification (optional)"
              value={form.justification}
              onChange={(e) => setForm({ ...form, justification: e.target.value })}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={!form.proposedName?.trim() || proposeMutation.isPending}
                onClick={() => proposeMutation.mutate()}
              >
                Create Draft
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
              {proposeMutation.isError && (
                <span className="text-xs text-red-500 self-center">
                  {(proposeMutation.error as Error).message}
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <p className="text-sm text-text-muted">Loading proposals...</p>
      ) : proposals.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <UserPlus size={40} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">No hiring proposals yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {proposals.map((p: HireProposal) => (
            <Card key={p.id}>
              <CardContent className="py-4">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
                      <UserPlus size={20} className="text-accent" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-text-primary">{p.proposedName}</p>
                      <p className="text-xs text-text-muted capitalize">
                        {p.proposedRole}{p.proposedTitle ? ` \u00b7 ${p.proposedTitle}` : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={statusVariant[p.status] ?? "default"}>
                      {p.status.replace(/_/g, " ")}
                    </Badge>
                    {expanded === p.id ? <ChevronUp size={16} className="text-text-muted" /> : <ChevronDown size={16} className="text-text-muted" />}
                  </div>
                </div>

                {expanded === p.id && (
                  <div className="mt-3 border-t border-border pt-3 space-y-2">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                      <div>
                        <span className="text-text-muted">Target: </span>
                        <span className="text-text-secondary">
                          {p.hireTargetType === "new_azure_instance" ? "New Azure VM" : "Sub-agent"}
                        </span>
                      </div>
                      <div>
                        <span className="text-text-muted">Reports to: </span>
                        <span className="text-text-secondary">
                          {p.proposedReportsTo
                            ? agents.find((a) => a.id === p.proposedReportsTo)?.name ?? p.proposedReportsTo.slice(0, 8)
                            : "None"}
                        </span>
                      </div>
                      {p.justification && (
                        <div className="col-span-2">
                          <span className="text-text-muted">Justification: </span>
                          <span className="text-text-secondary">{p.justification}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-text-muted">Created: </span>
                        <span className="text-text-secondary">{new Date(p.createdAt).toLocaleDateString()}</span>
                      </div>
                      {p.resultAgentId && (
                        <div>
                          <span className="text-text-muted">Agent ID: </span>
                          <span className="text-text-secondary font-mono">{p.resultAgentId.slice(0, 8)}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 pt-1">
                      {p.status === "draft" && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={submitMutation.isPending}
                          onClick={() => submitMutation.mutate(p.id)}
                        >
                          <Send size={14} className="mr-1.5" />
                          Submit for Approval
                        </Button>
                      )}
                      {p.status === "approved" && (
                        <Button
                          size="sm"
                          disabled={activateMutation.isPending}
                          onClick={() => activateMutation.mutate(p.id)}
                        >
                          <Rocket size={14} className="mr-1.5" />
                          Activate
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </CenterPanel>
  );
}
